import React from 'react';

interface LineItem {
  product_name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: string;
  total: string;
  is_promo?: boolean;
  // Phase 3 unified display fields (fall back to legacy fields when absent)
  description_display?: string;
  display_quantity?: string;
  display_unit_price?: string;
  display_bag_size_lbs?: number;
  display_form?: string;
}

interface Payment {
  date: string;
  method: string;
  reference: string;
  amount: string;
}

interface Credit {
  description: string;
  amount: string;
  date: string | null;
}

interface InvoiceData {
  company: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
    website: string;
  };
  customer: {
    company_name: string;
    contact_name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
  };
  shipping: {
    company_name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  invoice: {
    invoice_number: string;
    invoice_date: string;
    due_date: string | null;
    status: string;
    terms: string;
    is_overdue: boolean;
  };
  order: {
    order_number: string;
    po_number: string | null;
    order_date: string | null;
    delivery_date: string | null;
    shipping_method: string;
    special_instructions: string;
    notes: string;
  };
  line_items: LineItem[];
  totals: {
    subtotal: string;
    shipping: string;
    promo_discount_amount?: string;
    discount_amount?: string;
    shipping_discount?: string;
    tax: string;
    total: string;
    credits_applied: string;
    customer_credits_applied?: string;
    credit_applications?: {
      amount_applied: string;
      applied_at: string | null;
      credit_type: string;
      credit_type_display: string;
      reference: string;
    }[];
    amount_paid: string;
    balance_due: string;
  };
  discount_category_name?: string;
  payments: Payment[];
  credits: Credit[];
}

interface Props {
  data: InvoiceData;
  onClose?: () => void;
  onPrint?: () => void;
}

const PrintableInvoice: React.FC<Props> = ({ data, onClose, onPrint }) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  const isPaid = data.invoice.status === 'paid' || parseFloat(data.totals.balance_due) <= 0;
  const hasCredits = parseFloat(data.totals.credits_applied) > 0;
  const hasCustomerCredits = parseFloat(data.totals.customer_credits_applied || '0') > 0;
  const hasPaid = parseFloat(data.totals.amount_paid) > 0;

  return (
    <div style={styles.container}>
      {/* Print controls - hidden when printing */}
      <div style={styles.printControls} className="no-print">
        <button onClick={handlePrint} style={styles.printButton}>
          Print Invoice
        </button>
        {onClose && (
          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        )}
      </div>

      {/* Invoice document */}
      <div style={styles.invoice} className="invoice-print-area">
        {/* PAID watermark */}
        {isPaid && (
          <div style={styles.paidWatermark}>PAID</div>
        )}

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.companyInfo}>
            <h1 style={styles.companyName}>{data.company.name}</h1>
            <p style={styles.companyAddress}>
              {data.company.address}<br />
              {data.company.city}, {data.company.state} {data.company.zip}
            </p>
            <p style={styles.companyContact}>
              {data.company.phone}<br />
              {data.company.email}
            </p>
          </div>
          <div style={styles.invoiceTitle}>
            <h2 style={styles.invoiceLabel}>INVOICE</h2>
            <p style={styles.invoiceNumber}>{data.invoice.invoice_number}</p>
          </div>
        </div>

        {/* Addresses */}
        <div style={styles.addressSection}>
          <div style={styles.addressBlock}>
            <h3 style={styles.addressLabel}>BILL TO:</h3>
            <p style={styles.addressName}>{data.customer.company_name}</p>
            {data.customer.contact_name && (
              <p style={styles.addressLine}>Attn: {data.customer.contact_name}</p>
            )}
            <p style={styles.addressLine}>{data.customer.address}</p>
            <p style={styles.addressLine}>
              {data.customer.city}, {data.customer.state} {data.customer.zip}
            </p>
            {data.customer.phone && (
              <p style={styles.addressLine}>{data.customer.phone}</p>
            )}
            {data.customer.email && (
              <p style={styles.addressLine}>{data.customer.email}</p>
            )}
          </div>
          <div style={styles.addressBlock}>
            <h3 style={styles.addressLabel}>SHIP TO:</h3>
            <p style={styles.addressName}>{data.shipping.company_name}</p>
            <p style={styles.addressLine}>{data.shipping.address}</p>
            <p style={styles.addressLine}>
              {data.shipping.city}, {data.shipping.state} {data.shipping.zip}
            </p>
          </div>
        </div>

        {/* Invoice details */}
        <div style={styles.detailsSection}>
          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Invoice Date:</span>
              <span style={styles.detailValue}>{formatDate(data.invoice.invoice_date)}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Order #:</span>
              <span style={styles.detailValue}>{data.order.order_number}</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Due Date:</span>
              <span style={{
                ...styles.detailValue,
                color: data.invoice.is_overdue ? '#dc2626' : 'inherit',
                fontWeight: data.invoice.is_overdue ? 600 : 400,
              }}>
                {formatDate(data.invoice.due_date)}
                {data.invoice.is_overdue && ' (OVERDUE)'}
              </span>
            </div>
            {data.order.po_number && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>PO #:</span>
                <span style={styles.detailValue}>{data.order.po_number}</span>
              </div>
            )}
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>Terms:</span>
              <span style={styles.detailValue}>{data.invoice.terms}</span>
            </div>
            {data.order.delivery_date && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Delivery:</span>
                <span style={styles.detailValue}>{formatDate(data.order.delivery_date)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Line items */}
        <div style={styles.lineItemsSection}>
          <table style={styles.lineItemsTable}>
            <thead>
              <tr>
                <th style={styles.thProduct}>Product</th>
                <th style={styles.thQty}>Qty</th>
                <th style={styles.thPrice}>Unit Price</th>
                <th style={styles.thTotal}>Total</th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((item, index) => (
                <tr key={index}>
                  <td style={styles.tdProduct}>
                    {/* Single unified line — matches the server PDF, which emits
                        one display_description string. Falls back to the legacy
                        product_name + description when description_display is absent. */}
                    <strong>
                      {item.description_display
                        ?? `${item.product_name}${item.description ? ' ' + item.description : ''}`}
                    </strong>
                    {item.is_promo && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', background: '#dcfce7', color: '#166534', borderRadius: 4, fontSize: '0.7em', fontWeight: 700 }}>PROMO</span>
                    )}
                  </td>
                  <td style={styles.tdQty}>{item.quantity} {item.unit}</td>
                  <td style={styles.tdPrice}>${item.unit_price}/{item.unit === 'lbs' ? 'lb' : item.unit.slice(0, -1)}</td>
                  <td style={styles.tdTotal}>
                    {item.is_promo ? (
                      <span style={{ textDecoration: 'line-through', color: '#999' }}>{formatCurrency(item.total)}</span>
                    ) : (
                      formatCurrency(item.total)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={styles.totalsSection} className="invoice-totals">
          <div style={styles.totalsTable}>
            <div style={styles.totalsRow}>
              <span>Subtotal:</span>
              <span>{formatCurrency(data.totals.subtotal)}</span>
            </div>
            {data.totals.promo_discount_amount && parseFloat(data.totals.promo_discount_amount) > 0 && (
              <div style={{ ...styles.totalsRow, color: '#dc2626' }}>
                <span>Promo Discount:</span>
                <span>-{formatCurrency(data.totals.promo_discount_amount)}</span>
              </div>
            )}
            {data.totals.shipping_discount && parseFloat(data.totals.shipping_discount) > 0 && (
              <div style={{ ...styles.totalsRow, color: '#dc2626' }}>
                <span>Shipping Discount:</span>
                <span>-{formatCurrency(data.totals.shipping_discount)}</span>
              </div>
            )}
            {data.totals.discount_amount && parseFloat(data.totals.discount_amount) > 0 && (
              <div style={{ ...styles.totalsRow, color: '#dc2626' }}>
                <span>Discount{data.discount_category_name ? ` (${data.discount_category_name})` : ''}:</span>
                <span>-{formatCurrency(data.totals.discount_amount)}</span>
              </div>
            )}
            <div style={styles.totalsRow}>
              <span>Shipping:</span>
              <span>{formatCurrency(data.totals.shipping)}</span>
            </div>
            <div style={styles.totalsRow}>
              <span>Tax:</span>
              <span>{formatCurrency(data.totals.tax)}</span>
            </div>
            <div style={styles.totalLine}>
              <span>TOTAL:</span>
              <span>{formatCurrency(data.totals.total)}</span>
            </div>
            {hasCredits && (
              <div style={{ ...styles.totalsRow, color: '#7c3aed' }}>
                <span>Bridge Club Credit:</span>
                <span>-{formatCurrency(data.totals.credits_applied)}</span>
              </div>
            )}
            {hasCustomerCredits && (
              <div style={{ ...styles.totalsRow, color: '#7c3aed' }}>
                <span>Account Credit:</span>
                <span>-{formatCurrency(data.totals.customer_credits_applied || '0')}</span>
              </div>
            )}
            {hasPaid && (
              <div style={{ ...styles.totalsRow, color: '#22c55e' }}>
                <span>Amount Paid:</span>
                <span>-{formatCurrency(data.totals.amount_paid)}</span>
              </div>
            )}
            <div style={styles.balanceLine}>
              <span>BALANCE DUE:</span>
              <span style={{ color: isPaid ? '#22c55e' : '#dc2626' }}>
                {formatCurrency(data.totals.balance_due)}
              </span>
            </div>
          </div>
        </div>

        {/* Itemized account-credit applications (so a reduced balance is
            explained, not a random deduction) */}
        {data.totals.credit_applications && data.totals.credit_applications.length > 0 && (
          <div style={styles.notesSection}>
            <h3 style={styles.notesLabel}>CREDITS APPLIED TO THIS INVOICE:</h3>
            {data.totals.credit_applications.map((app, i) => (
              <p key={i} style={styles.notesText}>
                {formatDate(app.applied_at)} · {app.credit_type_display} · -{formatCurrency(app.amount_applied)}
              </p>
            ))}
          </div>
        )}

        {/* Special instructions */}
        {data.order.special_instructions && (
          <div style={styles.notesSection}>
            <h3 style={styles.notesLabel}>NOTES:</h3>
            <p style={styles.notesText}>{data.order.special_instructions}</p>
          </div>
        )}

        {/* Payment history */}
        {data.payments.length > 0 && (
          <div style={styles.paymentsSection}>
            <h3 style={styles.paymentsLabel}>PAYMENT HISTORY</h3>
            <table style={styles.paymentsTable}>
              <thead>
                <tr>
                  <th style={styles.paymentTh}>Date</th>
                  <th style={styles.paymentTh}>Method</th>
                  <th style={styles.paymentTh}>Reference</th>
                  <th style={{ ...styles.paymentTh, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment, index) => (
                  <tr key={index}>
                    <td style={styles.paymentTd}>{formatDate(payment.date)}</td>
                    <td style={styles.paymentTd}>{payment.method}</td>
                    <td style={styles.paymentTd}>{payment.reference || '—'}</td>
                    <td style={{ ...styles.paymentTd, textAlign: 'right' }}>{formatCurrency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer} className="invoice-footer">
          <p style={styles.footerThank}>Thank you for your business!</p>
          <p style={styles.footerPayment}>
            Payment Methods: Credit Card, ACH Bank Transfer
          </p>
          <p style={styles.footerWebsite}>
            Pay online at: {data.company.website}
          </p>
        </div>
      </div>

      {/* Print styles — only print the invoice, hide everything else */}
      <style>{`
        @media print {
          /* Hide everything on the page */
          body * {
            visibility: hidden;
          }
          /* Show only the invoice */
          .invoice-print-area,
          .invoice-print-area * {
            visibility: visible;
          }
          .invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 1.5rem;
            box-shadow: none;
          }
          /* Multi-page support */
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }
          .invoice-print-area {
            overflow: visible !important;
            max-height: none !important;
            height: auto !important;
          }
          /* Table page breaks */
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          /* Keep totals/footer together */
          .invoice-totals, .invoice-footer {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#f3f4f6',
    minHeight: '100%',
    padding: '1rem',
  },
  printControls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  printButton: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: '#2D5A27',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  closeButton: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 500,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  invoice: {
    backgroundColor: 'white',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    position: 'relative' as const,
    fontFamily: 'Georgia, serif',
  },
  paidWatermark: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-30deg)',
    fontSize: '8rem',
    fontWeight: 700,
    color: 'rgba(34, 197, 94, 0.15)',
    pointerEvents: 'none' as const,
    zIndex: 1,
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '3px solid #8B0000',
    paddingBottom: '1.5rem',
    marginBottom: '1.5rem',
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#8B0000',
    margin: '0 0 0.5rem 0',
    fontFamily: 'Arial, sans-serif',
  },
  companyAddress: {
    margin: '0 0 0.25rem 0',
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.4,
  },
  companyContact: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.4,
  },
  invoiceTitle: {
    textAlign: 'right' as const,
  },
  invoiceLabel: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: '0 0 0.25rem 0',
    fontFamily: 'Arial, sans-serif',
  },
  invoiceNumber: {
    fontSize: '1.1rem',
    color: '#4b5563',
    margin: 0,
    fontFamily: 'monospace',
  },
  addressSection: {
    display: 'flex',
    gap: '3rem',
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  addressBlock: {
    flex: 1,
  },
  addressLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#6b7280',
    margin: '0 0 0.5rem 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  addressName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
    margin: '0 0 0.25rem 0',
  },
  addressLine: {
    fontSize: '0.9rem',
    color: '#4b5563',
    margin: '0 0 0.125rem 0',
  },
  detailsSection: {
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem 2rem',
  },
  detailItem: {
    display: 'flex',
    gap: '0.5rem',
  },
  detailLabel: {
    fontSize: '0.85rem',
    color: '#6b7280',
    fontWeight: 500,
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#1f2937',
  },
  lineItemsSection: {
    marginBottom: '1.5rem',
  },
  lineItemsTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  thProduct: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #1f2937',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  thQty: {
    textAlign: 'center' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #1f2937',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  thPrice: {
    textAlign: 'right' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #1f2937',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  thTotal: {
    textAlign: 'right' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #1f2937',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  tdProduct: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top' as const,
  },
  tdQty: {
    textAlign: 'center' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top' as const,
  },
  tdPrice: {
    textAlign: 'right' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top' as const,
  },
  tdTotal: {
    textAlign: 'right' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top' as const,
    fontWeight: 600,
  },
  itemDescription: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  totalsSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  totalsTable: {
    width: '280px',
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.375rem 0',
    fontSize: '0.9rem',
  },
  totalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '1rem',
    fontWeight: 700,
    borderTop: '1px solid #e5e7eb',
    marginTop: '0.25rem',
  },
  balanceLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    fontSize: '1.1rem',
    fontWeight: 700,
    borderTop: '2px solid #1f2937',
    marginTop: '0.5rem',
  },
  notesSection: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '4px',
  },
  notesLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#6b7280',
    margin: '0 0 0.5rem 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  notesText: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#4b5563',
    lineHeight: 1.5,
  },
  paymentsSection: {
    marginBottom: '1.5rem',
  },
  paymentsLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#6b7280',
    margin: '0 0 0.75rem 0',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontFamily: 'Arial, sans-serif',
  },
  paymentsTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  paymentTh: {
    textAlign: 'left' as const,
    padding: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    color: '#6b7280',
  },
  paymentTd: {
    padding: '0.5rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#4b5563',
  },
  footer: {
    textAlign: 'center' as const,
    paddingTop: '1.5rem',
    borderTop: '3px solid #8B0000',
  },
  footerThank: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#1f2937',
    margin: '0 0 0.5rem 0',
  },
  footerPayment: {
    fontSize: '0.9rem',
    color: '#4b5563',
    margin: '0 0 0.25rem 0',
  },
  footerWebsite: {
    fontSize: '0.9rem',
    color: '#8B0000',
    margin: 0,
    fontWeight: 500,
  },
};

export default PrintableInvoice;
